import type { ReactNode } from 'react'
import { Info } from 'lucide-react'
import type { ConversationViewProps } from '@dev-streetbeat/chat-ui'
import type { Turn, TurnContent } from '@dev-streetbeat/chat/types'
import {
  BreathingRoomLayout,
  BreathingRoomPlaceholder,
  TurnComponent,
  TurnContentRenderer,
  useBreathingRoom,
} from '@dev-streetbeat/chat-ui/advanced'

import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { ChipPreview } from './composer-context/ChipPreview'

import styles from './CredemConversationView.module.scss'

const CLIENT_CONTEXT_LINE_RE = /^Clienti allegati:\s*(?<code>[^\s(]+)(?:\s*\((?<tag>[^)]+)\))?\s*$/m
const CLIENT_CONTEXT_CHIP_TAG = 'PRIVATE BANKER'
const FONTI_POSTFIX_RE = /(?:^|\n)[ \t]*Fonti:[ \t]*(?<sources>[^\n]*(?:\n(?!\s*\n).*)*)\s*$/

function splitFontiPostfix(value: string): { body: string; sources: string } | null {
  const match = value.match(FONTI_POSTFIX_RE)
  if (!match?.groups?.sources) return null
  const body = value.slice(0, match.index).trimEnd()
  const sources = match.groups.sources.trim()
  if (!body || !sources) return null
  return { body, sources }
}

function interruptedTextFor(
  interruptedText: NonNullable<ConversationViewProps['rendering']>['interruptedText'] | undefined,
  turn: Turn,
): string | undefined {
  if (typeof interruptedText === 'function') return interruptedText(turn)
  return interruptedText
}

function renderClientAwareBlock(block: TurnContent, turn: Turn, index: number): ReactNode {
  if (block.type !== 'text') {
    return <TurnContentRenderer block={block} turn={turn} blockIndex={index} />
  }

  if (turn.role === 'assistant') {
    const fonti = splitFontiPostfix(block.value)
    if (!fonti) {
      return <TurnContentRenderer block={block} turn={turn} blockIndex={index} />
    }

    const cleanedBlock: TurnContent = {
      ...block,
      value: fonti.body,
    }

    return (
      <span className={styles.sourceAwareContent}>
        <span className={styles.sourceTextBody}>
          <TurnContentRenderer block={cleanedBlock} turn={turn} blockIndex={index} />
        </span>
        <span className={styles.sourceChipRow}>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="outline" tabIndex={0} className="cursor-help">
                  <Info aria-hidden />
                  Fonti
                </Badge>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                align="start"
                className="w-[min(320px,calc(100vw-2rem))] flex-col items-start gap-1.5"
              >
                <span>
                  <span className="font-semibold">Fonte:</span>{' '}
                  <code className="break-words font-mono text-[11px] leading-relaxed">
                    {fonti.sources}
                  </code>
                </span>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </span>
      </span>
    )
  }

  if (turn.role !== 'user') {
    return <TurnContentRenderer block={block} turn={turn} blockIndex={index} />
  }

  const match = block.value.match(CLIENT_CONTEXT_LINE_RE)
  if (!match?.groups?.code) {
    return <TurnContentRenderer block={block} turn={turn} blockIndex={index} />
  }

  const cleanedValue = block.value
    .replace(CLIENT_CONTEXT_LINE_RE, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
  const cleanedBlock: TurnContent = {
    ...block,
    value: cleanedValue,
  }

  return (
    <span className={styles.clientTurnContent}>
      {cleanedValue ? (
        <span className={styles.clientTextBody}>
          <TurnContentRenderer block={cleanedBlock} turn={turn} blockIndex={index} />
        </span>
      ) : null}
      <span className={styles.clientChipRow}>
        <ChipPreview label={match.groups.code} tag={CLIENT_CONTEXT_CHIP_TAG} />
      </span>
    </span>
  )
}

export function CredemConversationView({
  turns,
  isStreaming = false,
  isLoading = false,
  isCancelling = false,
  breathingRoomEnabled = false,
  breathingRoomThinkingLabel,
  scrollMarginTop = 32,
  maxWidth = '46rem',
  messageActions,
  artifacts,
  rendering,
  agentName,
  agentIcon,
  threadId,
  className,
}: ConversationViewProps) {
  const breathingRoom = useBreathingRoom({ turns, isStreaming, isLoading, isCancelling })
  const interruptedIds = new Set(rendering?.interruptedTurnIds ?? [])

  const renderTurn = (turn: Turn, index: number): ReactNode => {
    const nextTurn = turns[index + 1]
    const sendTimestamp =
      turn.role === 'assistant' && index > 0 ? String(turns[index - 1]?.createdAt ?? '') : undefined
    return (
      <div key={turn.id} data-turn-id={turn.id}>
        <TurnComponent
          turn={turn}
          agentName={agentName}
          agentIcon={agentIcon}
          threadId={threadId}
          messageActions={messageActions}
          sendTimestamp={sendTimestamp}
          showThinking={rendering?.showThinking}
          showTools={rendering?.showTools}
          showArtifacts={rendering?.showArtifacts}
          stickyPlan={rendering?.stickyPlan}
          thinkingLabel="Sto pensando..."
          thinkingStreamingLabel="Sto pensando..."
          secondaryThinkingLabel="Sto riflettendo più a fondo..."
          secondaryCompletedLabel="Completato"
          secondaryWorkingLabel="Sto pensando..."
          artifacts={artifacts}
          renderBlock={renderClientAwareBlock}
          isInterrupted={interruptedIds.has(turn.id)}
          interruptedText={interruptedTextFor(rendering?.interruptedText, turn)}
          compact={!breathingRoom.wasStreamedInSession(turn.id)}
          turnRenderKey={turn.optimistic ? `optimistic-${turn.id}-${index}` : turn.id}
          {...(turn.role === 'user' && breathingRoomEnabled && nextTurn?.role === 'assistant'
            ? { alwaysShowMessageActions: false }
            : {})}
        />
      </div>
    )
  }

  const renderTurns = (): ReactNode[] => {
    const nodes: ReactNode[] = []
    let lastUserTurnIndex = -1

    for (let index = turns.length - 1; index >= 0; index -= 1) {
      if (turns[index].role === 'user') {
        lastUserTurnIndex = index
        break
      }
    }

    for (let index = 0; index < turns.length; index += 1) {
      const turn = turns[index]
      const nextTurn = turns[index + 1]
      const isLastTurn = index === turns.length - 1
      const isLastUserTurn = index === lastUserTurnIndex
      const trailingAssistant =
        isLastUserTurn && nextTurn?.role === 'assistant' ? nextTurn : undefined
      const trailingAssistantWasStreamed =
        trailingAssistant !== undefined && breathingRoom.wasStreamedInSession(trailingAssistant.id)
      const shouldReserveBreathingRoom =
        breathingRoomEnabled &&
        turn.role === 'user' &&
        isLastUserTurn &&
        ((isLastTurn &&
          (breathingRoom.showBreathingRoomPlaceholder || breathingRoom.isAwaitingAgentResponse)) ||
          (trailingAssistant !== undefined &&
            (breathingRoom.showBreathingRoomPlaceholder ||
              breathingRoom.isAgentStreaming ||
              trailingAssistantWasStreamed)))

      if (!shouldReserveBreathingRoom) {
        nodes.push(renderTurn(turn, index))
        continue
      }

      const spacerContent =
        trailingAssistant !== undefined ? (
          renderTurn(trailingAssistant, index + 1)
        ) : (
          <BreathingRoomPlaceholder
            thinkingLabel={breathingRoomThinkingLabel}
            sendTimestamp={String(turn.createdAt)}
          />
        )

      nodes.push(
        <BreathingRoomLayout
          key={turn.id}
          turnId={turn.id}
          scrollMarginTop={scrollMarginTop}
          spacerContent={spacerContent}
        >
          {renderTurn(turn, index)}
        </BreathingRoomLayout>,
      )

      if (trailingAssistant !== undefined) {
        index += 1
      }
    }

    return nodes
  }

  return (
    <div
      className={`${styles.conversation}${className ? ` ${className}` : ''}`}
      style={{ maxWidth }}
    >
      <div className={styles.turns}>{renderTurns()}</div>
    </div>
  )
}
